import { useState, useEffect } from 'react';
import Button from 'components/v2/button/Button';
import OnboardingLayout from '../layouts/OnboardingLayout';
import StepCard from '../StepCard';
import DayItem from '../DayItem';
import { DaysOfWeek, TimeSlots } from '../utils/constants';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { PlusIcon } from 'lucide-react';
import { IUser, IAvailabilityPeriod } from 'modules/auth/types/types';
import { useUpdateUserMutation } from 'modules/auth/services/user.mutation.service';
import { Availability, AvailabilityPeriod } from '../utils/types';
import CustomTimeSlot from '../CustomTimeSlot';
import { IStartAndEndTimeSlot } from '../CustomTimeSlot';
import TimeSlotsList from '../TimeSlotsList';
import { AvailabilityPeriodModel } from 'modules/profile/model/Period';
import { v4 as uuidv4 } from 'uuid';

export interface TimeSlot {
  label: string;
  prefix: string;
  startTime: string | Date;
  endTime: string | Date;
}

interface SelectYourAvailabilityProps {
  onBackClick: () => void;
  phoneNumber?: string;
  name?: string;
  userName?: string;
  photoURL?: string | null;
  onboardingProgress: number;
}

interface DayTimeSelections {
  [day: string]: {
    selectedTimes: TimeSlot[];
    customTimeSlots: IStartAndEndTimeSlot[];
  };
}

const SelectYourAvailability: React.FC<SelectYourAvailabilityProps> = ({
  onBackClick,
  phoneNumber,
  name,
  userName,
  photoURL,
  onboardingProgress,
}): JSX.Element => {
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();
  const [availability, setAvailability] = useState<Availability | null>({});
  const [selectedDays, setSelectedDays] = useState<string[]>([]);

  const [dayTimeSelections, setDayTimeSelections] = useState<DayTimeSelections>(
    {},
  );

  const sortedSelectedDays = selectedDays.sort((a, b) => {
    return (
      DaysOfWeek.findIndex((day) => day.label === a) -
      DaysOfWeek.findIndex((day) => day.label === b)
    );
  });

  useEffect(() => {
    const newDayTimeSelections = { ...dayTimeSelections };

    selectedDays.forEach((day) => {
      if (!newDayTimeSelections[day]) {
        newDayTimeSelections[day] = {
          selectedTimes: [],
          customTimeSlots: [],
        };
      }
    });

    Object.keys(newDayTimeSelections).forEach((day) => {
      if (!selectedDays.includes(day)) {
        delete newDayTimeSelections[day];
      }
    });

    setDayTimeSelections(newDayTimeSelections);
  }, [selectedDays]);

  // Update availability state whenever dayTimeSelections changes
  useEffect(() => {
    const updatedAvailability: Availability = {};

    Object.entries(dayTimeSelections).forEach(([day, selections]) => {
      const periods: AvailabilityPeriod[] = [];

      // Add selected times
      if (selections.selectedTimes.length > 0) {
        selections.selectedTimes.forEach((time) => {
          // Handle both string and Date objects for startTime and endTime
          const startDateTime =
            typeof time.startTime === 'string'
              ? time.startTime
              : time.startTime instanceof Date
                ? time.startTime.toISOString()
                : dayjs(time.startTime).toISOString();

          const endDateTime =
            typeof time.endTime === 'string'
              ? time.endTime
              : time.endTime instanceof Date
                ? time.endTime.toISOString()
                : dayjs(time.endTime).toISOString();

          // Create an AvailabilityPeriodModel instance
          const periodModel = new AvailabilityPeriodModel(
            [],
            false,
            uuidv4(),
            startDateTime,
            endDateTime,
            'Europe/Kiev',
            'Europe/Kiev',
          );

          // Add to periods
          periods.push(periodModel as unknown as AvailabilityPeriod);
        });
      }

      // Add custom time slots
      if (selections.customTimeSlots.length > 0) {
        selections.customTimeSlots.forEach((slot) => {
          // Create an AvailabilityPeriodModel instance
          const periodModel = new AvailabilityPeriodModel(
            [],
            false,
            uuidv4(),
            slot.start.dateTime,
            slot.end?.dateTime || slot.start.dateTime,
            'Europe/Kiev',
            'Europe/Kiev',
          );

          // Add to periods
          periods.push(periodModel as unknown as AvailabilityPeriod);
        });
      }

      if (periods.length > 0) {
        updatedAvailability[day as keyof Availability] = periods;
      }
    });

    setAvailability(updatedAvailability);
  }, [dayTimeSelections]);

  const handleDaySelect = (day: string): void => {
    setSelectedDays((prev) => {
      const newDays = prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day];

      return newDays;
    });
  };

  const handleTimeSelect = (day: string, time: TimeSlot): void => {
    setDayTimeSelections((prev) => {
      const daySelections = prev[day] || {
        selectedTimes: [],
        customTimeSlots: [],
      };
      const isTimeSelected = daySelections.selectedTimes.some(
        (t) => t.label === time.label,
      );

      let updatedTimes;
      if (isTimeSelected) {
        updatedTimes = daySelections.selectedTimes.filter(
          (t) => t.label !== time.label,
        );
      } else {
        updatedTimes = [...daySelections.selectedTimes, time];
      }

      return {
        ...prev,
        [day]: {
          ...daySelections,
          selectedTimes: updatedTimes,
        },
      };
    });
  };

  const handleAddCustomTimeSlot = (day: string): void => {
    setDayTimeSelections((prev) => {
      const daySelections = prev[day] || {
        selectedTimes: [],
        customTimeSlots: [],
      };

      const updatedDaySelections = {
        selectedTimes: daySelections.selectedTimes,
        customTimeSlots: [
          ...daySelections.customTimeSlots,
          {
            start: {
              dateTime: new Date().toISOString(),
              timeZone: 'UTC',
            },
            end: {
              dateTime: new Date().toISOString(),
              timeZone: 'UTC',
            },
          },
        ],
      };

      return {
        ...prev,
        [day]: updatedDaySelections,
      };
    });
  };

  const handleDeleteCustomTimeSlot = (day: string, index: number): void => {
    setDayTimeSelections((prev) => {
      const daySelections = prev[day] || {
        selectedTimes: [],
        customTimeSlots: [],
      };

      return {
        ...prev,
        [day]: {
          ...daySelections,
          customTimeSlots: daySelections.customTimeSlots.filter(
            (_, i) => i !== index,
          ),
        },
      };
    });
  };

  const handleSelectAllDays = (): void => {
    if (selectedDays.length === DaysOfWeek.length) {
      setSelectedDays([]);
    } else {
      setSelectedDays(DaysOfWeek.map((day) => day.label));
    }
  };

  const handleSubmit = (): void => {
    // Convert availability to the format expected by the API
    const availabilityForApi = new Map<string, IAvailabilityPeriod[]>();

    if (availability) {
      Object.entries(availability).forEach(([day, periods]) => {
        if (periods && periods.length > 0) {
          // Convert the periods to IAvailabilityPeriod using AvailabilityPeriodModel
          const periodsWithId = periods.map((period): IAvailabilityPeriod => {
            if (period.allDay) {
              // Use the static method for all-day periods
              return AvailabilityPeriodModel.allDay() as unknown as IAvailabilityPeriod;
            } else {
              // Create a new period with the AvailabilityPeriodModel class
              return new AvailabilityPeriodModel(
                [],
                period.allDay,
                period.id || uuidv4(),
                period.start.dateTime,
                period.end?.dateTime || period.start.dateTime,
              ) as unknown as IAvailabilityPeriod;
            }
          });

          // Convert day name to uppercase (e.g., "Monday" to "MONDAY")
          const uppercaseDay = day.toUpperCase();
          availabilityForApi.set(uppercaseDay, periodsWithId);
        }
      });
    }

    // Include user data from previous steps
    const userData: Partial<IUser> = {
      availability: availabilityForApi,
    };

    // Add user details if provided
    if (name) userData.name = name;
    if (userName) userData.username = userName.replace('@', '');
    if (phoneNumber)
      userData.phoneNumber = `+1${phoneNumber.replace(/\D/g, '')}`;

    // Handle profile image if needed
    if (photoURL) userData.photoURL = photoURL;

    // Create serializable version of userData for API request
    const userDataForApi = { ...userData };

    // Convert Map to a plain object for proper serialization
    if (userDataForApi.availability instanceof Map) {
      const availabilityObj: Record<string, IAvailabilityPeriod[]> = {};
      userDataForApi.availability.forEach((value, key) => {
        availabilityObj[key] = value;
      });
      // @ts-ignore - We need to override the type for API serialization
      userDataForApi.availability = availabilityObj;
    }

    // Update user with all collected information
    updateUser({
      data: userDataForApi as IUser,
    })
      .unwrap()
      .then(() => {
        // Redirect to home page after successful submission, with a flag to refresh user data
        navigate('/', { state: { refreshUser: true } });
      })
      .catch((error) => {
        console.error('Error updating user:', error);
      });
  };

  const hasDaysSelected = selectedDays.length > 0;

  return (
    <OnboardingLayout
      onBackClick={onBackClick}
      actionChildren={
        <div>
          <Button onClick={handleSubmit}>Done</Button>
        </div>
      }
      onboardingProgress={onboardingProgress}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-8">
          <div className="max-w-[65%] text-[2rem] font-bold font-manrope">
            What days are usually free for hangouts?
          </div>
          <div className="flex flex-col gap-5 mb-[5rem]">
            <StepCard title="Select days" step={1}>
              <div className="flex flex-wrap gap-2">
                {DaysOfWeek.map((day) => (
                  <DayItem
                    key={day.label}
                    day={day.prefix}
                    isSelected={selectedDays.includes(day.label)}
                    onSelect={() => handleDaySelect(day.label)}
                  />
                ))}
              </div>
              <div
                className="flex flex-col items-start gap-2 cursor-pointer"
                onClick={handleSelectAllDays}
              >
                <div className="text-[0.875rem] border border-[#ABE49C] text-[#ABE49C] rounded-full px-4 py-3 font-semibold">
                  Select all
                </div>
              </div>
            </StepCard>

            <StepCard title="Choose preferred time(s) of day" step={2}>
              <div className="flex flex-col gap-12">
                {!hasDaysSelected && (
                  <div className="flex flex-col gap-4">
                    <TimeSlotsList
                      timeSlots={TimeSlots as unknown as TimeSlot[]}
                      isDisabled={true}
                    />
                  </div>
                )}

                {hasDaysSelected &&
                  sortedSelectedDays.map((day) => {
                    const daySelections = dayTimeSelections[day] || {
                      selectedTimes: [],
                      customTimeSlots: [],
                    };
                    const hasCustomSlots =
                      daySelections.customTimeSlots.length > 0;
                    const hasSelectedTimesForDay =
                      daySelections.selectedTimes.length > 0;

                    return (
                      <div key={day} className="flex flex-col gap-4">
                        <div className="flex justify-between gap-2">
                          <div className="text-[0.875rem] font-semibold">
                            {day}
                          </div>
                          <div className="flex flex-col gap-2">
                            <TimeSlotsList
                              timeSlots={TimeSlots as unknown as TimeSlot[]}
                              selectedTimes={daySelections.selectedTimes}
                              isDisabled={hasCustomSlots}
                              onTimeSelect={(time) =>
                                handleTimeSelect(day, time)
                              }
                              day={day}
                            />
                            <div className="text-[0.875rem] font-semibold pt-2">
                              Or
                            </div>
                            {daySelections.customTimeSlots.map(
                              (time, index) => (
                                <div
                                  key={index}
                                  className="flex flex-col gap-4"
                                >
                                  <CustomTimeSlot
                                    selectedCustomTimeSlots={
                                      daySelections.customTimeSlots
                                    }
                                    setSelectedCustomTimeSlots={(slots) => {
                                      setDayTimeSelections((prev) => ({
                                        ...prev,
                                        [day]: {
                                          ...prev[day],
                                          customTimeSlots: slots,
                                        },
                                      }));
                                    }}
                                    start={{
                                      dateTime: new Date().toISOString(),
                                      timeZone: 'UTC',
                                    }}
                                    onDelete={() =>
                                      handleDeleteCustomTimeSlot(day, index)
                                    }
                                    onTimeChange={() => {}}
                                  />
                                </div>
                              ),
                            )}

                            <div
                              className={`max-w-48 text-[0.875rem] border border-[#ABE49C] text-[#ABE49C] rounded-full pr-4 pl-3 py-3 font-semibold flex items-center gap-1 font-manrope whitespace-nowrap ${
                                hasSelectedTimesForDay
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'cursor-pointer'
                              }`}
                              onClick={() => {
                                if (!hasSelectedTimesForDay) {
                                  handleAddCustomTimeSlot(day);
                                }
                              }}
                            >
                              <PlusIcon className="w-4 h-4" /> Add specific time
                              slot
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </StepCard>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default SelectYourAvailability;
